import React, { useCallback, useEffect, useRef, useState } from "react";
import { Check, ChevronLeft, ChevronRight, Clock, MapPin, MessageCircle, MessageSquare, Phone, RefreshCw, Users, X } from "lucide-react";
import { Groupe, TitreGroupe, Retour, Vide, Badge, Segments } from "../espace/ui";
import { CalendrierAnnee } from "../components/CalendrierAnnee";
import { api, erreurDe, aujourdhui, decaler, jourLisible, majuscule, plusTard, sms, tel } from "./commun";

/* ── L'appel d'une séance, enregistré à l'école ────────────────────────────── */
const Bouton = ({ actif, couleur, onClick, label, children, testId }) => (
  <button onClick={onClick} aria-label={label} aria-pressed={actif} data-testid={testId}
    className="w-11 h-11 rounded-full grid place-items-center shrink-0 transition-all active:scale-90"
    style={actif ? { background: couleur, color: "#fff" } : { border: "2px solid #E2E8F0", color: "#CBD5E1" }}>
    {children}
  </button>
);

const Eleve = ({ p, essai, etat, onEtat, verrouille }) => {
  const [ouvert, setOuvert] = useState(false);
  return (
    <div data-testid="prof-eleve">
      <div className="flex items-center gap-3 px-4 py-3">
        <button onClick={() => setOuvert(!ouvert)} className="flex-1 min-w-0 text-left">
          <span className="block text-[15.5px] leading-snug"
            style={etat === "absent" ? { color: "#94A3B8" } : undefined}>
            {p.eleve}{p.age ? <span className="text-[var(--muted)]"> · {p.age} ans</span> : null}
          </span>
          <span className="flex flex-wrap gap-1.5 mt-1">
            {essai && <Badge couleur="#00A8AF">Essai</Badge>}
            {p.absent && <Badge couleur="#F59E0B">Absence annoncée</Badge>}
            {p.nouveau && <Badge couleur="#2563EB">Nouveau</Badge>}
            {/* Arrivé·e par une fusion de cours : pas nouveau·elle à l'école. */}
            {p.fusion && <Badge couleur="#64748B">Fusion{p.fusion.avant ? ` · avant ${p.fusion.avant}` : ""}</Badge>}
            {/* Carte 10 cours : une case à tamponner sur la carte papier à chaque séance. */}
            {p.carte10 && (p.carte10_etat
              // Carte 10 numérique : séances utilisées, ou « terminée » quand il en faut une nouvelle.
              ? <Badge couleur={p.carte10_etat.statut === "en_cours" ? "#0D9488" : "#DC2626"}>
                {p.carte10_etat.statut === "en_cours" ? `Carte ${p.carte10_etat.n}/${p.carte10_etat.nb}` : "Carte 10 terminée"}
                {p.carte10_etat.hors_carte ? ` · ${p.carte10_etat.hors_carte} hors carte` : ""}</Badge>
              : <Badge couleur="#B45309">Carte 10 cours</Badge>)}
            {p.anniversaire && <Badge couleur="#DB2777">🎂 Anniversaire</Badge>}
            {p.desinscrit && <Badge couleur="#94A3B8">Dernier cours bientôt</Badge>}
            {String(p.droit_image || "").toLowerCase().startsWith("n") && <Badge couleur="#DC2626">Pas de photo</Badge>}
          </span>
          {/* Le motif d'une absence peut être long : sur sa propre ligne, il passe à la ligne
              au lieu de sortir de l'écran (école, 30.10.2026). */}
          {p.absent && p.absence_motif && (
            <span className="block text-[12.5px] leading-snug mt-1 break-words" style={{ color: "#B45309" }}
              data-testid="prof-absence-motif-eleve">{p.absence_motif}</span>
          )}
        </button>
        {!verrouille && (
          <>
            <Bouton actif={etat === "absent"} couleur="#DC2626" label="Absent" testId="prof-absent"
              onClick={() => onEtat(etat === "absent" ? "" : "absent")}><X size={19} strokeWidth={2.6} /></Bouton>
            <Bouton actif={etat === "present"} couleur="#16A34A" label="Présent" testId="prof-present"
              onClick={() => onEtat(etat === "present" ? "" : "present")}><Check size={19} strokeWidth={2.8} /></Bouton>
          </>
        )}
      </div>
      {ouvert && (
        <div className="px-4 pb-3 -mt-1 text-[13px] space-y-2">
          <div className="text-[var(--muted)]">{[p.responsable, p.infos].filter(Boolean).join(" · ") || "—"}</div>
          {p.telephone && (
            <div className="flex flex-wrap gap-2">
              <a href={tel(p.telephone)} className="btn-ghost text-xs inline-flex"><Phone size={13} /> Appeler {p.responsable?.split(" ")[0] || "le parent"}</a>
              <a href={sms(p.telephone)} className="btn-ghost text-xs inline-flex" data-testid="prof-ecrire-sms"><MessageSquare size={13} /> Écrire</a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const Appel = ({ seance, onRetour }) => {
  const [d, setD] = useState(null);
  const [etats, setEtats] = useState({});
  const [sauve, setSauve] = useState("");
  const [erreur, setErreur] = useState("");
  const premier = useRef(true);
  // Ce que l'école a déjà reçu : on n'envoie que les élèves qui changent (la direction
  // peut cocher les autres en même temps sans être écrasée).
  const envoye = useRef({});
  const futur = seance.date > aujourdhui();

  useEffect(() => {
    api.get(`/liste/${encodeURIComponent(seance.cours_id)}?le=${seance.date}`)
      .then(({ data }) => { setD(data); setEtats(data.appel || {}); envoye.current = { ...(data.appel || {}) }; if (data.appel_le) setSauve("Appel enregistré"); })
      .catch((e) => setErreur(erreurDe(e)));
  }, [seance]);

  // Chaque coche part à l'école (petite pause pour grouper les taps) : rien à « sauver ».
  useEffect(() => {
    if (premier.current || !d) { premier.current = !d; return; }
    setSauve("Enregistrement…");
    plusTard("appel", async () => {
      const diff = {};
      Object.keys({ ...envoye.current, ...etats }).forEach((k) => {
        if ((etats[k] || "") !== (envoye.current[k] || "")) diff[k] = etats[k] || "";
      });
      if (!Object.keys(diff).length) { setSauve("Appel enregistré"); return; }
      try {
        const { data } = await api.put(`/appel/${encodeURIComponent(seance.cours_id)}/${d.le}`, { etats: diff });
        envoye.current = { ...envoye.current, ...diff };
        setSauve(`Enregistré · ${data.presents} présent${data.presents > 1 ? "s" : ""}, ${data.absents} absent${data.absents > 1 ? "s" : ""}`);
        // Carte 10 : « Carte 7/10 » suit la coche tout de suite (avant : seulement en rouvrant l'appel).
        if (d.inscrits.some((p) => p.carte10_etat)) {
          api.get(`/liste/${encodeURIComponent(seance.cours_id)}?le=${d.le}`).then(({ data: x }) => {
            const cartes = Object.fromEntries((x.inscrits || []).map((p) => [p.eleve_id, p.carte10_etat]));
            setD((v) => ({ ...v, inscrits: v.inscrits.map((p) => (p.carte10_etat ? { ...p, carte10_etat: cartes[p.eleve_id] || p.carte10_etat } : p)) }));
          }).catch(() => { /* le badge se met à jour à la prochaine ouverture */ });
        }
      } catch (e) { setSauve(""); setErreur(erreurDe(e)); }
    });
  }, [etats]); // eslint-disable-line react-hooks/exhaustive-deps

  if (erreur && !d) return <><Retour onClick={onRetour} label="Mes cours" /><Vide>{erreur}</Vide></>;
  if (!d) return <div className="py-16 grid place-items-center"><RefreshCw className="animate-spin" size={20} /></div>;
  const tous = [...d.inscrits.map((p) => ({ p, essai: false })), ...d.essais.map((p) => ({ p, essai: true }))];
  const presents = Object.values(etats).filter((v) => v === "present").length;
  const absents = Object.values(etats).filter((v) => v === "absent").length;
  const mettre = (id, v) => setEtats((e) => { const s = { ...e }; if (v) s[id] = v; else delete s[id]; return s; });
  const tousPresents = () => setEtats(Object.fromEntries(tous.map(({ p }) => [p.eleve_id, p.absent ? "absent" : "present"])));

  return (
    <div className="space-y-5" data-testid="prof-appel">
      <Retour onClick={onRetour} label="Mes cours" />
      <div>
        <div className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12.5px] font-semibold"
          style={{ background: "#0F1B2D", color: "#fff" }} data-testid="prof-appel-date">
          {seance.date === aujourdhui() ? `Aujourd'hui · ${jourLisible(d.le)}` : majuscule(jourLisible(d.le))}
        </div>
        <div className="font-display text-[26px] leading-tight mt-2">{d.cours.nom}</div>
        <div className="text-[13px] text-[var(--muted)] mt-1 flex flex-wrap gap-x-3">
          <span className="inline-flex items-center gap-1"><Clock size={13} />{d.cours.debut}–{d.cours.fin}</span>
          {d.cours.salle && <span className="inline-flex items-center gap-1"><MapPin size={13} />{/^salle/i.test(d.cours.salle) ? d.cours.salle : `salle ${d.cours.salle}`}</span>}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-center">
        {[["Attendus", tous.length, "#0F1B2D"], ["Présents", presents, "#16A34A"], ["Absents", absents, "#DC2626"]].map(([l, n, c]) => (
          <div key={l} className="rounded-2xl bg-white py-3" style={{ border: "1px solid var(--border)" }}>
            <div className="text-[24px] font-semibold tabular-nums" style={{ color: c }}>{n}</div>
            <div className="text-[11.5px] text-[var(--muted)]">{l}</div>
          </div>
        ))}
      </div>

      {futur ? (
        <p className="text-[13px] rounded-2xl px-4 py-3" style={{ background: "#EFF6FF", color: "#1D4ED8" }}>
          L'appel s'ouvre le jour du cours. En attendant, voici qui est attendu.
        </p>
      ) : (
        <div className="flex items-center justify-between gap-3">
          <span className="text-[13px] text-[var(--muted)]" data-testid="prof-appel-etat">
            {sauve || "Touche ✓ ou ✗ pour chaque élève"}
          </span>
          <button onClick={tousPresents} className="btn-ghost text-xs shrink-0" data-testid="prof-tous-presents">Tous présents</button>
        </div>
      )}

      <Groupe>
        {tous.length ? tous.map(({ p, essai }) => (
          <Eleve key={`${p.eleve_id}-${essai}`} p={p} essai={essai} etat={etats[p.eleve_id] || ""}
            onEtat={(v) => mettre(p.eleve_id, v)} verrouille={futur} />
        )) : <p className="px-4 py-4 text-sm text-[var(--muted)]">Aucun élève pour cette séance.</p>}
      </Groupe>
      {seance.lien_whatsapp && (
        <a href={seance.lien_whatsapp} target="_blank" rel="noopener noreferrer" className="btn-ghost w-full justify-center">
          <MessageCircle size={15} /> Groupe WhatsApp du cours
        </a>
      )}
      {erreur && <p className="text-sm" style={{ color: "#B91C1C" }}>{erreur}</p>}
    </div>
  );
};

/* ── Mes cours : le prochain, puis la semaine, jour par jour ───────────────── */
const LigneSeance = ({ s, onOuvrir }) => {
  const vide = !s.ferme && s.eleves === 0;
  return (
    <button onClick={() => !s.ferme && onOuvrir(s)} disabled={s.ferme} data-testid={`prof-seance-${s.cours_id}`}
      className="w-full flex gap-3.5 px-4 py-3.5 text-left active:bg-[#F1F5F9]" style={s.ferme ? { opacity: 0.5 } : undefined}>
      <div className="w-[54px] shrink-0 self-start rounded-2xl text-center py-2" style={{ background: "var(--primary-tint)" }}>
        <div className="text-[15px] font-semibold tabular-nums leading-tight">{s.debut}</div>
        <div className="text-[11px] text-[var(--muted)] tabular-nums">{s.fin}</div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[15px] font-semibold leading-snug">{s.cours}</div>
        <div className="text-[12.5px] text-[var(--muted)] mt-0.5">{s.salle ? `salle ${s.salle}` : ""}{s.note ? `${s.salle ? " · " : ""}${s.note}` : ""}</div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {s.ferme ? <Badge couleur="#94A3B8">Pas de cours (fermé)</Badge>
            // Qui le remplace : « remplacé·e » s'affichait même quand l'école n'avait encore
            // trouvé personne (tests de l'école, 01.11.2026).
            : s.je_suis_absent ? <Badge couleur="#F59E0B">{s.remplace_par ? `Tu es absent·e · remplacé·e par ${s.remplace_par}` : "Tu es absent·e · l'école organise le remplacement"}</Badge>
              : vide ? <Badge couleur="#DC2626">Aucun élève : pas besoin de venir</Badge>
                : <Badge couleur="#0F1B2D">{s.eleves} élève{s.eleves > 1 ? "s" : ""}</Badge>}
          {s.absents > 0 && <Badge couleur="#F59E0B">{s.absents} absent{s.absents > 1 ? "s" : ""} annoncé{s.absents > 1 ? "s" : ""}</Badge>}
          {s.appel_fait && <Badge couleur="#16A34A">Appel fait ✓</Badge>}
        </div>
      </div>
      {!s.ferme && <ChevronRight size={18} className="self-center shrink-0" style={{ color: "#C4C9D0" }} />}
    </button>
  );
};

const MOIS_NOMS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
// « d'octobre », « de novembre » (la carte disait « Tes heures de octobre »).
const deMois = (nom) => (/^[aeiou]/.test(nom) ? `d'${nom}` : `de ${nom}`);

/* « Tes heures » : des cours déjà donnés attendent leur ✓ / ✗ (ou le mois est fini et
   les heures pas encore envoyées). Un tap ouvre l'onglet Heures. */
const RappelHeures = ({ attente, onHeures }) => {
  if (!attente?.length) return null;
  const m = attente.find((x) => x.termine) || attente[0];
  const nom = MOIS_NOMS[Number(m.mois.slice(5, 7)) - 1];
  return (
    <button onClick={() => onHeures(m.mois)} data-testid="prof-rappel-heures"
      className="w-full text-left rounded-[22px] px-4 py-3.5 flex items-center gap-3 active:opacity-90"
      style={{ background: m.termine ? "#FEF3C7" : "#ECFEFF", border: `1px solid ${m.termine ? "#FDE68A" : "#A5F3FC"}` }}>
      <span className="w-9 h-9 rounded-full grid place-items-center shrink-0 text-white" style={{ background: m.termine ? "#D97706" : "#0891B2" }}>
        <Check size={18} strokeWidth={2.6} />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[15px] font-semibold leading-snug">
          {m.termine ? `Tes heures ${deMois(nom)} sont à envoyer` : `${m.a_cocher} cours donné${m.a_cocher > 1 ? "s" : ""} à confirmer`}
        </span>
        <span className="block text-[12.5px] text-[var(--muted)]">
          {m.termine ? (m.a_cocher ? `${m.a_cocher} cours à cocher, puis « Envoyer mes heures »` : "Tout est coché : touche « Envoyer mes heures »") : "Coche ✓ donné ou ✗ pas donné, au fil du mois"}
        </span>
      </span>
      <ChevronRight size={18} style={{ color: "#94A3B8" }} />
    </button>
  );
};

// La semaine affichée quand le prof ouvre une séance : au retour de l'appel (une séance de
// la semaine passée), il retrouve cette semaine-là et non « les 7 prochains jours ».
let semaineAuRetour = "";

export const Cours = ({ moi, onOuvrir: ouvrirSeance, onHeures }) => {
  const [debut, setDebut] = useState(aujourdhui());
  const [semaine, setSemaine] = useState(moi.semaine);
  const onOuvrir = (s) => { semaineAuRetour = debut; ouvrirSeance(s); };
  const [charge, setCharge] = useState(false);
  const [vue, setVue] = useState("semaine");
  // Au retour d'un appel, `moi` est rechargé : la semaine affichée doit suivre
  // (sinon « Appel fait ✓ » n'apparaissait jamais).
  useEffect(() => { if (debut === aujourdhui()) setSemaine(moi.semaine); }, [moi.semaine]); // eslint-disable-line react-hooks/exhaustive-deps
  const charger = useCallback(async (j) => {
    setDebut(j);
    if (j === aujourdhui()) { setSemaine(moi.semaine); return; }
    setCharge(true);
    try { const { data } = await api.get(`/semaine?debut=${j}`); setSemaine(data.semaine); } catch { /* garde l'ancienne */ }
    setCharge(false);
  }, [moi.semaine]);
  useEffect(() => {
    const j = semaineAuRetour;
    semaineAuRetour = "";
    if (j && j !== aujourdhui()) charger(j);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const auj = aujourdhui();
  const prochain = moi.semaine.find((s) => !s.ferme && !s.je_suis_absent && s.eleves > 0
    && (s.date > auj || (s.date === auj && (s.fin || "") >= new Date().toTimeString().slice(0, 5))));
  const jours = [...new Set(semaine.map((s) => s.date))];

  const choix = <Segments testId="prof-vue" valeur={vue} onChange={setVue}
    options={[{ id: "semaine", label: "Ma semaine" }, { id: "annee", label: "L'année" }]} />;
  if (vue === "annee") return <div className="space-y-5">{choix}<CalendrierAnnee testId="prof-annee" /></div>;

  return (
    <div className="space-y-6" data-testid="prof-cours">
      {choix}
      {debut === auj && <RappelHeures attente={moi.heures_a_verifier} onHeures={onHeures} />}
      {prochain && debut === auj && (
        <button onClick={() => onOuvrir(prochain)} className="w-full text-left rounded-[22px] p-5 text-white active:opacity-90"
          style={{ background: "linear-gradient(140deg,#0F1B2D 0%,#123243 60%,#00A8AF 100%)" }} data-testid="prof-prochain">
          <div className="text-[12px] uppercase tracking-[0.14em] font-semibold" style={{ color: "#66EAF0" }}>
            Prochain cours · {prochain.date === auj ? "aujourd'hui" : jourLisible(prochain.date)}
          </div>
          <div className="font-display text-[24px] leading-tight mt-2">{prochain.cours}</div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-[14px]">
            <span className="inline-flex items-center gap-1.5"><Clock size={14} />{prochain.debut}–{prochain.fin}</span>
            {prochain.salle && <span className="inline-flex items-center gap-1.5"><MapPin size={14} />{/^salle/i.test(prochain.salle) ? prochain.salle : `salle ${prochain.salle}`}</span>}
            <span className="inline-flex items-center gap-1.5"><Users size={14} />{prochain.eleves} élève{prochain.eleves > 1 ? "s" : ""}</span>
          </div>
          <div className="mt-4 inline-flex items-center gap-1 rounded-full px-4 py-2 text-[13.5px] font-semibold" style={{ background: "#fff", color: "#0F1B2D" }}>
            {prochain.date === auj ? "Faire l'appel" : "Voir la liste"} <ChevronRight size={15} />
          </div>
        </button>
      )}

      <div className="flex items-center justify-between rounded-[22px] bg-white px-2 py-1.5" style={{ border: "1px solid var(--border)" }}>
        <button onClick={() => charger(decaler(debut, -7))} aria-label="Semaine précédente" className="w-11 h-11 grid place-items-center rounded-full active:bg-[#F1F5F9]"><ChevronLeft size={20} /></button>
        <button onClick={() => charger(auj)} className="text-center">
          <div className="text-[15px] font-semibold">{debut === auj ? "Les 7 prochains jours" : `Dès le ${jourLisible(debut)}`}</div>
          <div className="text-[11.5px]" style={{ color: debut === auj ? "var(--primary)" : "var(--muted)" }}>
            {debut === auj ? "Mes cours de la semaine" : "Revenir à aujourd'hui"}
          </div>
        </button>
        <button onClick={() => charger(decaler(debut, 7))} aria-label="Semaine suivante" className="w-11 h-11 grid place-items-center rounded-full active:bg-[#F1F5F9]"><ChevronRight size={20} /></button>
      </div>

      {charge && <div className="py-6 grid place-items-center"><RefreshCw className="animate-spin" size={18} /></div>}
      {!charge && !semaine.length && <Vide>{debut === auj ? "Aucun cours ces 7 prochains jours." : "Aucun cours cette semaine-là."}</Vide>}
      {!charge && jours.map((j) => (
        <section key={j}>
          <TitreGroupe>{j === auj ? `Aujourd'hui · ${jourLisible(j)}` : jourLisible(j)}</TitreGroupe>
          <Groupe>
            {semaine.filter((s) => s.date === j).map((s) => <LigneSeance key={`${s.date}-${s.cours_id}`} s={s} onOuvrir={onOuvrir} />)}
          </Groupe>
        </section>
      ))}
    </div>
  );
};
